import SiteConfig from '../model/SiteConfig'
import ValidationError from '../common/ValidationError'
import { LoadSiteConfigList } from '../pages/SiteConfigList/actions/LoadSiteConfigList'
import { SaveSiteConfig } from '../pages/SiteConfig/actions/UpdateConfig'
import { ClearErrorMessage } from '../actions/ManageError'
import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { Success, Failure } from 'typescript-fsa'
import { SitesLoadData } from '../rest-api/SiteConfig-api'
import { IMessageService } from '../common/IMessageService'
import { ErrorMessage } from '../model/Message'

export type SiteConfigState = {
  markets: Map<string, MarketConfigState>
}

export type MarketConfigState = {
  siteConfigs: SiteConfig[]
  sitesLoading: boolean
  errorMessage: string
}

export const siteConfigInitialState = {
  markets: new Map<string, MarketConfigState>()
}
export const marketConfigInitialState = {
  sitesLoading: true,
  siteConfigs: [],
  errorMessage: ''
}
const getMarket = (state: SiteConfigState, market: string): MarketConfigState => {
  if(state.markets.has(market)) {
    return state.markets[market];
  } else {
    return marketConfigInitialState;
  }
}
export const createSiteConfigReducer = (messageService: IMessageService) => reducerWithInitialState<SiteConfigState>(siteConfigInitialState)
  .case(LoadSiteConfigList.started, (state: SiteConfigState, params: string) => ({
    ...state,
    markets: state.markets.set(params, ({
      ...getMarket(state, params),
      sitesLoading: true
    }))
  }))
  .case(
    LoadSiteConfigList.done,
    (state: SiteConfigState, payload: Success<string, SitesLoadData>) => ({
      ...state,
      markets: state.markets.set(payload.params, ({
          ...getMarket(state, payload.params),
          siteConfigs: payload.result.sites.filter(s => s.marketKey.toLowerCase() !== 'global'),
          sitesLoading: false
        }))
    })
  )
  .case(LoadSiteConfigList.failed, (state: SiteConfigState, code: Failure<string, string>) => {
    messageService.sendMessage(ErrorMessage(`Failed to load sites for ${code.params}`, code.error))
    return ({
    ...state,
    markets: state.markets.set(code.params, ({
        ...getMarket(state, code.params),
        errorMessage: `Error loading site list: ${code.error}`
      }))
  })})
  .case(SaveSiteConfig.done, (state: SiteConfigState, payload: Success<SiteConfig, SiteConfig>) => {
    const marketState = getMarket(state, payload.result.marketKey);
    return ({
    ...state,
    markets: state.markets.set(payload.result.marketKey, ({
      ...marketState,
      siteConfigs: marketState.siteConfigs.map((c: SiteConfig) =>
        c.key === payload.result.key ? {
          ...c,
          ...payload.result
        } : c
      )
    }))
  })})
  .case(
    SaveSiteConfig.failed,
    (
      state: SiteConfigState,
      error: Failure<SiteConfig, { message: string; errors: ValidationError[] }>
    ) => {
      const marketState = getMarket(state, error.params.marketKey);
      return error.error.message !== 'Validation Failed'
        ? { ...state, errorMessage: `Error saving site: ${error.error.message}` }
        : {
          ...state,
          markets: state.markets.set(error.params.marketKey, ({
            ...marketState,
            siteConfigs: marketState.siteConfigs.map((c: SiteConfig) =>
              c.key === error.params.key
                ? (assignValidation(c, error.error.errors) as SiteConfig)
                : c
            )
          }))
          
        }
      }
  )
  .case(ClearErrorMessage, (state: SiteConfigState) => ({ ...state, errorMessage: '' }))

// this method mutates the object, rather than returning a modified copy, this could be bad
function assignValidation(obj: object, validation: ValidationError[]) {
  obj['validation'] = validation
  for (const propertyName of Object.keys(obj)) {
    var propertyValidation = validation.filter(
      error => error.field && error.field.toLowerCase().match(`^${propertyName.toLowerCase()}\\.`)
    )
    if (propertyValidation.length > 0) {
      propertyValidation = propertyValidation.map(error => {
        return {
          field: error.field && error.field.substr(propertyName.length + 1),
          message: error.message
        }
      })
      assignValidation(obj[propertyName], propertyValidation)
    }
  }
  // return a new object here so that downstream methods know this thing has changed
  return { ...obj }
}
